import { useState, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  Box,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MasterEntryEditor from "./MasterEntryEditor";
import { LookupValueContext } from "../../common/contexts/LookupValueContext";
import { deleteMasterCodeLegendEntry } from "../../api/APIHelpers";
import { AlertsContext } from "../../common/contexts/AlertsContext";
import { TenantContext } from "../../common/contexts/TenantContext";
import { extractApiErrorMessage } from "../../common/helpers/ErrorUtils";

const MasterEntryConfigurator = ({ masterEntries }) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetEntry, setDeleteTargetEntry] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { setInfoMessages } = useContext(AlertsContext);
  const tenantInfo = useContext(TenantContext);

  const lookupValues = useContext(LookupValueContext);
  const alarmStateObj = lookupValues?.find(
    (lv) => lv.LookupName === "AlarmState"
  );
  const alarmStates =
    alarmStateObj?.Values?.map((value) => ({
      id: value.LookupValueId,
      name: value.DisplayName,
    })) || [];
  const isMechObj = lookupValues?.find((lv) => lv.LookupName === "IsMech");
  const isMechOptions =
    isMechObj?.Values?.map((value) => ({
      id: value.LookupValueId,
      name: value.DisplayName,
    })) || [];

  const filteredEntries = masterEntries.filter((entry) =>
    entry.Description?.toLowerCase().includes(search.toLowerCase())
  );
  const paginatedEntries = filteredEntries.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Blank new master entry object
  const blankMasterEntry = {
    CodeLegendMasterEntryId: 0,
    Description: "",
    AlarmStateId: null,
    IsMechId: null,
    SendAlerts: false,
    TenantId: tenantInfo.currentTenantId,
    AltDescriptions: [],
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEditor = (entry) => {
    setSelectedEntry(entry);
    setEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
    setSelectedEntry(null);
  };

  const handleDeleteEntry = (entry) => {
    setDeleteTargetEntry(entry);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteMasterCodeLegendEntry(
        deleteTargetEntry.CodeLegendMasterEntryId
      );
      setInfoMessages({
        type: "snackbar",
        message: "Standard Code Description deleted successfully.",
        severity: "success",
        open: true,
      });
    } catch (err) {
      console.error(err);
      setInfoMessages({
        type: "snackbar",
        message: `Error deleting Standard Code Description: ${extractApiErrorMessage(err)}`,
        severity: "error",
        open: true,
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDeleteTargetEntry(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetEntry(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h5' gutterBottom>
        Standard Code Descriptions
      </Typography>
      <TextField
        label='Search by Description'
        variant='outlined'
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          if (page !== 0) {
            setPage(0);
          }
        }}
        sx={{ mb: 2, width: 300 }}
      />
      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell>Alarm State</TableCell>
              <TableCell>Is Mechanical</TableCell>
              <TableCell>Send Alerts</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedEntries.length > 0 ? (
              paginatedEntries.map((entry) => (
                <TableRow key={entry.CodeLegendMasterEntryId}>
                  <TableCell>{entry.Description}</TableCell>
                  <TableCell>
                    {alarmStates.find(
                      (state) => state.id === entry.AlarmStateId
                    )?.name || ""}
                  </TableCell>
                  <TableCell>
                    {isMechOptions.find(
                      (option) => option.id === entry.IsMechId
                    )?.name || ""}
                  </TableCell>
                  <TableCell>{entry.SendAlerts ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    <IconButton
                      aria-label='edit'
                      color='primary'
                      onClick={() => handleOpenEditor(entry)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label='delete'
                      color='error'
                      onClick={() => handleDeleteEntry(entry)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align='center'>
                  No master entries found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component='div'
          count={filteredEntries.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage='Rows per page:'
        />
      </TableContainer>
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            setSelectedEntry(blankMasterEntry);
            setEditorOpen(true);
          }}>
          Add New Standard Code Description
        </Button>
      </Box>
      <Dialog
        open={editorOpen}
        onClose={handleCloseEditor}
        maxWidth='md'
        fullWidth>
        <DialogTitle>Edit Standard Code Description</DialogTitle>
        <DialogContent>
          {selectedEntry && <MasterEntryEditor entry={selectedEntry} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditor}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Standard Code Description</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this Standard Code Description?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color='error'
            disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MasterEntryConfigurator;
